import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Avatar, EmptyState, ErrorState, Spinner } from "@/components/common";
import { ConnectButton } from "@/components/connect/ConnectButton";
import { api, apiError, INTEREST_KEYS, TYPE_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";

const TABS = [["members", "All members"], ["requests", "Requests"], ["connections", "My connections"]];

const MemberCard = ({ m, onChange, note }) => (
  <li className="surface card-hover flex flex-col gap-4 p-5" data-testid={`connect-card-${m.id}`}>
    <Link to={`/members/${m.id}`} className="flex items-center gap-4" data-testid={`connect-profile-link-${m.id}`}>
      <Avatar person={m} size={64} />
      <div className="min-w-0">
        <p className="truncate font-display text-2xl leading-tight">{m.name}</p>
        <p className="truncate text-sm text-muted-foreground">{m.home_city}</p>
      </div>
    </Link>
    {note && <p className="rounded-md bg-secondary p-3 text-sm">“{note}”</p>}
    {!note && m.bio && <p className="line-clamp-2 text-sm text-foreground/80">{m.bio}</p>}
    {m.interests?.length > 0 && (
      <div className="flex flex-wrap gap-1.5">{m.interests.slice(0, 3).map((i) => <span key={i} className="rounded-md bg-secondary px-2 py-0.5 text-xs">{TYPE_LABELS[i] || i}</span>)}</div>
    )}
    <div className="mt-auto flex items-center justify-between gap-3">
      <Link to={`/members/${m.id}`} className="text-sm text-primary underline-offset-4 hover:underline">View profile</Link>
      <ConnectButton member={m} onChange={onChange} compact />
    </div>
  </li>
);

const Grid = ({ children, testId }) => <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={testId}>{children}</ul>;

const AllMembers = ({ onAnyChange }) => {
  const [q, setQ] = useState("");
  const [interest, setInterest] = useState("");
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    api.get("/members", { params: { q, interest } }).then(({ data }) => { setList(data.filter((m) => m.connection_state !== "self")); setError(""); }).catch((e) => setError(apiError(e)));
  }, [q, interest]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const update = (id) => (s) => { setList((l) => l.map((m) => (m.id === id ? { ...m, connection_state: s.state, connection_id: s.connection_id ?? m.connection_id } : m))); onAnyChange(); };
  return (
    <>
      <div className="flex flex-col gap-4">
        <label className="relative block max-w-xl">
          <span className="sr-only">Search members</span>
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input className="field pl-12" placeholder="Search by name, city or bio" value={q} onChange={(e) => setQ(e.target.value)} data-testid="connect-search-input" />
        </label>
        <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filter by interest">
          {[["", "Everyone"], ...INTEREST_KEYS.map((k) => [k, TYPE_LABELS[k]])].map(([k, label]) => (
            <button key={label} type="button" className="chip shrink-0" data-active={interest === k} aria-pressed={interest === k} onClick={() => setInterest(k)} data-testid={`connect-filter-${k || "all"}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="mt-8">
        {error ? <ErrorState message={error} onRetry={load} /> : !list ? <Spinner /> : list.length === 0
          ? <EmptyState title="No members found" body="Try a different name, city or interest." testId="connect-empty" />
          : <Grid testId="connect-results">{list.map((m) => <MemberCard key={m.id} m={m} onChange={update(m.id)} />)}</Grid>}
      </div>
    </>
  );
};

const Mine = ({ data, reload, kind }) => {
  const items = kind === "requests" ? [...data.incoming.map((x) => ({ ...x, state: "pending_in" })), ...data.outgoing.map((x) => ({ ...x, state: "pending_out" }))]
    : data.connections.map((x) => ({ ...x, state: "connected" }));
  if (!items.length) return <EmptyState title={kind === "requests" ? "No requests right now" : "No connections yet"} body="Find members in All members and send a request." testId={`connect-${kind}-empty`} />;
  return (
    <Grid testId={`connect-${kind}-list`}>
      {items.map((x) => <MemberCard key={x.id} m={{ ...x.user, connection_state: x.state, connection_id: x.id }} note={x.state === "pending_in" ? x.message : ""} onChange={reload} />)}
    </Grid>
  );
};

export default function Connect() {
  const [tab, setTab] = useState("members");
  const [mine, setMine] = useState(null);
  const loadMine = useCallback(() => api.get("/connections").then(({ data }) => setMine(data)).catch(() => {}), []);
  useEffect(() => { loadMine(); }, [loadMine]);
  const incoming = mine?.incoming.length || 0;
  return (
    <div className="shell pb-24 pt-10" data-testid="connect-page">
      <p className="eyebrow">Connect</p>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl">Meet the members.</h1>
      <p className="mt-3 max-w-xl text-muted-foreground">Search everyone in the club, review their profiles and send a request to connect.</p>
      <div className="mt-8 flex gap-6 border-b border-border" role="tablist">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`connect-tab-${id}`}
            className={cn("-mb-px border-b-2 pb-3 text-sm sm:text-base", tab === id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
            {label}{id === "requests" && incoming > 0 && <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground" data-testid="connect-requests-count">{incoming}</span>}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "members" ? <AllMembers onAnyChange={loadMine} /> : !mine ? <Spinner /> : <Mine data={mine} reload={loadMine} kind={tab} />}
      </div>
    </div>
  );
}
